import moment from "moment-timezone"
import { isSortingEvent } from "./EventStage"
import { formatEventDate } from "./EventPresentation"
import { foldLocationText } from "./eventLocationOptions"
import { tenis10CategoryLabel } from "./tenis10Event"

// A competition without fees takes no registration until its fees are saved (Events Planner ticket 25). The reads say so with
// `feesSet`; an older API without the field marks nothing. A disabled competition takes no registration anyway.
export const competitionsWithoutFees = competitions => (competitions || [])
  .filter(competition => competition?.feesSet === false && !["DELETED", "DISABLED"].includes(competition.status))

// Events awaiting approval, disabled or deleted take no registration, so their competitions need no warning (as the API counts them).
const HIDDEN_EVENT_STATUSES = ["PENDING", "DISABLED", "DELETED"]

// The warning about an event's competitions without fees, as the API counts it (Events feesAttention.js): grey "notSet" until one
// calendar week, in the event's time zone, before its registrations open (the pre-registration for a Sorting event, never a Tenis10 one),
// "warning" from then, "closed" once they opened, nothing once the event ended. `phases` holds the registration phases by name.
export function feesWarning({ status, category, programType, timezone, endDate, phases: stored }, competitions, now = Date.now()) {
  const missing = competitionsWithoutFees(competitions)
  if (!missing.length || HIDDEN_EVENT_STATUSES.includes(status) || (endDate && moment(endDate).valueOf() < now)) return null
  const phases = stored || {}
  const zone = moment.tz.zone(timezone) ? timezone : "UTC"
  const preRegistration = isSortingEvent({ category, programType }) && Boolean(phases.preRegistrationsOpen?.startDate)
  const opening = preRegistration ? phases.preRegistrationsOpen.startDate : phases.registrationsOpen?.startDate
  const opensAt = opening && moment(opening, moment.ISO_8601, true).isValid() ? moment(opening).tz(zone) : null
  const warningFrom = opensAt && opensAt.clone().subtract(1, "week")
  const state = !opensAt || now < warningFrom.valueOf() ? "notSet" : now < opensAt.valueOf() ? "warning" : "closed"
  return { state, missing, preRegistration, timezone: zone, opensAt: opensAt?.toISOString(), warningFrom: warningFrom?.toISOString() }
}

// Whether the warning is on (red): from a calendar week before the opening, and after it.
export const feesWarned = warning => warning?.state === "warning" || warning?.state === "closed"
// The mark of each competition without fees in a list of competitions.
export const competitionFeesMark = warning => warning?.state === "closed" ? "Registrations closed: no fees" : feesWarned(warning) ? "No fees" : "Fees not set"

// An Events list row: its registration phases come as `registrationPhase` and `preRegistrationPhase`.
export const directoryFeesWarning = (event, now) => feesWarning({ ...event,
  phases: { registrationsOpen: event.registrationPhase, preRegistrationsOpen: event.preRegistrationPhase } }, event.competitions, now)

export const competitionCount = count => count + (count === 1 ? " competition" : " competitions")
export const openingText = warning => (warning.preRegistration ? "pre-registration opens " : "registrations open ")
  + formatEventDate(warning.opensAt, warning.timezone)
// „5S (#80005)”; a Tenis10 competition by its category, „Verde · U10 (#80009)”, never by the adult level the schema also gives it
export const competitionLabel = competition => (competition.tenis10Level ? tenis10CategoryLabel(competition)
  : `${competition.level ?? "—"}${competition.gameType === "singles" ? "S" : competition.gameType === "doubles" ? "D" : ""}`)
  + ` (#${competition.competitionId ?? "—"})`

// The fee template "Set fees for all competitions" preselects (ticket 25): by the category (the editor read offers only its templates),
// the season of the event's start date (summer April–October, winter November–March) and the zone of the club's county (B-CJ for
// București and Cluj, Other elsewhere), as the templates are keyed ("winterB-CJ"); a category with one template ("default") takes it.
export const feeTemplateSeason = (startDate, timezone) => {
  const month = moment(startDate).tz(moment.tz.zone(timezone) ? timezone : "UTC").month() + 1
  return month >= 4 && month <= 10 ? "summer" : "winter"
}
export const feeTemplateZone = club => ["B", "CJ"].includes(club?.location?.countyCode)
  || ["bucuresti", "cluj"].includes(foldLocationText(club?.location?.county)) ? "B-CJ" : "Other"
export const preferredFeeTemplate = (templates, { startDate, timezone, club }) => {
  const key = startDate && moment(startDate).isValid() ? feeTemplateSeason(startDate, timezone) + feeTemplateZone(club) : null
  return templates.some(template => template.key === key) ? key : templates.length === 1 ? templates[0].key : ""
}
